import React, { useEffect, useState } from 'react';
import { getClients, createClient, updateClient, toggleClientActive, getUsers, registerUser, toggleUserActive } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Power, Users, Building2, Loader2 } from 'lucide-react';

export default function ClientsPage() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (err) {
      toast.error(t('entities.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t('common.required'));
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateClient(editing.id, form);
        toast.success(t('common.updatedOk'));
      } else {
        await createClient(form);
        toast.success(t('common.createdOk'));
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', contact_email: '', phone: '' });
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('entities.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, contact_email: client.contact_email || '', phone: client.phone || '' });
    setShowForm(true);
  };

  const handleToggleActive = async (clientId) => {
    try {
      await toggleClientActive(clientId);
      loadClients();
      toast.success(t('entities.stateChanged'));
    } catch (err) {
      toast.error(t('entities.errorToggle'));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="clients-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">{t('entities.clients')}</h1>
            <p className="text-muted-foreground">{clients.length} {t('entities.clients').toLowerCase()}</p>
          </div>
          <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { setEditing(null); setForm({ name: '', contact_email: '', phone: '' }); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-btn">
                <Plus size={16} className="mr-2" />
                {t('entities.addClient')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? t('entities.editClient') : t('entities.addClient')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>{t('common.name')} *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    placeholder={t('common.name')}
                    className="mt-1"
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label>{t('auth.email')}</Label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({...form, contact_email: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{t('entities.phone')}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    placeholder="+52 123 456 7890"
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={saving} data-testid="save-client-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full dense-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                <th className="py-2 px-3 font-medium">{t('common.name')}</th>
                <th className="py-2 px-3 font-medium">{t('auth.email')}</th>
                <th className="py-2 px-3 font-medium">{t('entities.phone')}</th>
                <th className="py-2 px-3 font-medium">{t('common.status')}</th>
                <th className="py-2 px-3 font-medium w-24">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No hay clientes registrados</td></tr>
              ) : (
                clients.map((client) => (
                  <tr key={client.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`client-row-${client.id}`}>
                    <td className="py-2 px-3 font-medium">{client.name}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{client.contact_email || '—'}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{client.phone || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${client.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {client.is_active ? t('users.active') : t('users.inactive')}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(client)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleToggleActive(client.id)} className={client.is_active ? 'text-amber-400' : 'text-emerald-400'}>
                          <Power size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
